'use client';

import React, { Fragment, useEffect, useState } from 'react';
import { ApiRoutes, Routes } from '@/settings/routes';
import axios from '@/lib/axios';
import RecentPost from '@/components/front/ui/RecentPost';
import PaginationFront from '@/components/front/ui/PaginationFront';
import Link from 'next/link';
import CardTopDeal from '@/components/front/ui/CardTopDeal';
import { App_text } from '@/settings/AppText';
import LoadingFront from '@/components/front/ui/LoadingFront';
import { PiCalendarBlank, PiArrowRight } from 'react-icons/pi';
import { format } from 'date-fns';

export default function ContentBlogPage() {
    const [loading, setLoading] = useState(true);
    const [featured, setFeatured] = useState([]);
    const [articles, setArticles] = useState([]);
    const [recent, setRecent] = useState([]);
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [tours, setTours] = useState([]);
    const [activities, setActivities] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [paginationLinks, setPaginationLinks] = useState(null);

    const fetchData = async (url = ApiRoutes.API_get_articles_front) => {
        setLoading(true);
        try {
            const res = await axios.get(url);
            if (res.data.status === 200) {
                setFeatured(res.data.featured_posts || []);
                setArticles(res.data.articles?.data || []);
                setPagination(res.data.articles || null);
                setPaginationLinks(res.data.articles?.links || null);
                setRecent(res.data.recent_posts || []);
                setCategories(res.data.categories || []);
                setTags(res.data.tags || []);
                setTours(res.data.tours_promotion || []);
                setActivities(res.data.activities_promotion || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const nextData = (url) => fetchData(`${url.replace(ApiRoutes.API_ROUT, '')}`);
    const prevData = (url) => fetchData(`${url.replace(ApiRoutes.API_ROUT, '')}`);
    const pageData = (page) => fetchData(`${ApiRoutes.API_get_articles_front}?page=${page}`);

    useEffect(() => {
        fetchData();
    }, []);

    const displayFeatured = featured.length > 0 ? featured : articles.slice(0, 3);
    const featuredIds = displayFeatured.map((f) => f.id);
    const filteredArticles = articles.filter((a) => !featuredIds.includes(a.id));

    return (
        <div className="relative px-4 pb-10 mx-auto mt-10 w-full max-w-8xl lg:px-8 sm:pb-12 lg:pb-16 lg:mt-16">
            {loading ? (
                <LoadingFront />
            ) : articles.length === 0 && featured.length === 0 ? (
                <div className="p-8 text-center text-gray-600 rounded bg-custom-orange-100">No articles available.</div>
            ) : (
                <>
                    {displayFeatured.length > 0 && (
                        <section className="mb-14">
                            <h2 className="flex gap-2 items-center mb-6 text-xl font-bold default-color-title">
                                <span className="w-1 h-6 rounded-full bg-main-color"></span>
                                Featured Articles
                            </h2>
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                                {displayFeatured.map((article) => {
                                    const img = article?.miniature ? ApiRoutes.ASSETS_ENDPOINT + article.miniature : 'https://placehold.co/600x400';
                                    const date = article?.created_at || article?.updated_at;
                                    return (
                                        <Link
                                            key={article.id}
                                            prefetch={false}
                                            href={Routes.FRONT_blog_article + article.slug}
                                            className="group relative overflow-hidden rounded-lg bg-gray-900 min-h-[280px] flex items-end"
                                        >
                                            <img src={img} alt={article.title} className="object-cover absolute inset-0 w-full h-full opacity-70 transition-all duration-500 group-hover:opacity-90 group-hover:scale-105" />
                                            <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/80 via-black/40" />
                                            <div className="relative z-10 p-5">
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {(article?.categories || []).slice(0, 2).map((c, i) => (
                                                        <span key={i} className="text-xs font-medium text-white bg-main-color/90 px-2 py-0.5 rounded">
                                                            {c.name}
                                                        </span>
                                                    ))}
                                                </div>
                                                <h3 className="mb-2 text-lg font-semibold leading-snug text-white line-clamp-2">{article.title}</h3>
                                                <div className="flex gap-2 items-center text-xs text-white/70">
                                                    <PiCalendarBlank className="w-3.5 h-3.5" />
                                                    {date && format(new Date(date), 'MMM dd, yyyy')}
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    <div className="grid grid-cols-12 gap-8 lg:gap-10">
                        <main className="col-span-12 xl:col-span-8">
                            <h2 className="flex gap-2 items-center mb-6 text-xl font-bold default-color-title">
                                <span className="w-1 h-6 rounded-full bg-custom-green"></span>
                                Latest Articles
                            </h2>

                            <div className="flex flex-col gap-6">
                                {filteredArticles.map((article) => {
                                    const img = article?.miniature ? ApiRoutes.ASSETS_ENDPOINT + article.miniature : 'https://placehold.co/400x300';
                                    const date = article?.created_at || article?.updated_at;
                                    return (
                                        <article key={article.id} className="flex overflow-hidden flex-col bg-white rounded-lg border transition-shadow group sm:flex-row hover:shadow-md">
                                            <div className="overflow-hidden flex-shrink-0 h-48 bg-gray-100 sm:w-64 sm:h-auto">
                                                <Link prefetch={false} href={Routes.FRONT_blog_article + article.slug}>
                                                    <img src={img} alt={article.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
                                                </Link>
                                            </div>
                                            <div className="flex flex-col flex-grow justify-center p-5">
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {(article?.categories || []).slice(0, 2).map((c, i) => (
                                                        <Link key={i} prefetch={false} href={Routes.FRONT_blog_category + c.slug} className="text-xs font-medium text-main-color hover:underline">
                                                            {c.name}
                                                        </Link>
                                                    ))}
                                                </div>
                                                <Link prefetch={false} href={Routes.FRONT_blog_article + article.slug}>
                                                    <h3 className="mb-2 text-xl font-semibold transition-colors default-color-title group-hover:text-main-color line-clamp-2">{article.title}</h3>
                                                </Link>
                                                {article?.excerpt && <p className="mb-3 text-sm text-gray-500 line-clamp-2">{article.excerpt}</p>}
                                                <div className="flex justify-between items-center">
                                                    <span className="flex items-center gap-1.5 text-xs text-gray-400">
                                                        <PiCalendarBlank className="w-4 h-4" />
                                                        {date && format(new Date(date), 'MMMM dd, yyyy')}
                                                    </span>
                                                    <Link prefetch={false} href={Routes.FRONT_blog_article + article.slug} className="flex gap-1 items-center text-sm font-medium transition-all text-main-color hover:gap-2">
                                                        Read more <PiArrowRight className="w-4 h-4" />
                                                    </Link>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>

                            {pagination && paginationLinks && (
                                <nav className="mt-10">
                                    <PaginationFront pagination={pagination} paginationLinks={paginationLinks} prevPage={prevData} nextPage={nextData} page={pageData} />
                                </nav>
                            )}
                        </main>

                        <aside className="col-span-12 xl:col-span-4">
                            <div className="flex flex-col gap-5 xl:sticky xl:top-6">
                                {categories.length > 0 && (
                                    <div className="p-5 rounded-lg bg-custom-orange-100">
                                        <h3 className="mb-3 text-base font-semibold default-color-title">{App_text.blog.sidebar.title_categories}</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {categories.map((cat, idx) => (
                                                <Link
                                                    key={cat.id || idx}
                                                    prefetch={false}
                                                    href={Routes.FRONT_blog_category + cat.slug}
                                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white rounded text-sm text-gray-700 hover:bg-main-color hover:text-white transition-colors"
                                                >
                                                    {cat.name}
                                                    <span className="text-xs text-gray-400">({cat.articles_count ?? 0})</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* {recent.length > 0 && (
                                    <div className="p-5 bg-white rounded-lg border">
                                        <h3 className="mb-3 text-base font-semibold default-color-title">{App_text.blog.sidebar.title_posts}</h3>
                                        <div className="flex flex-col gap-3">
                                            {recent.map((a, idx) => (
                                                <Fragment key={a.id || idx}>
                                                    <RecentPost data={a} />
                                                    {idx !== recent.length - 1 && <hr className="border-gray-100" />}
                                                </Fragment>
                                            ))}
                                        </div>
                                    </div>
                                )} */}

                                {tags.length > 0 && (
                                    <div className="p-5 bg-white rounded-lg border">
                                        <h3 className="mb-3 text-base font-semibold default-color-title">{App_text.blog.sidebar.title_tags}</h3>
                                        <div className="flex flex-wrap gap-1.5">
                                            {tags.map((tag, idx) => (
                                                <Link
                                                    key={tag.id || idx}
                                                    prefetch={false}
                                                    href={Routes.FRONT_blog_tag + tag.slug}
                                                    className="text-xs px-2.5 py-1 border rounded text-gray-500 hover:border-main-color hover:text-main-color transition-colors"
                                                >
                                                    #{tag.name}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </aside>
                    </div>
                </>
            )}
        </div>
    );
}
